<template>
    <canvas
        ref="canvas"
        class="neural-wave"
        aria-hidden="true" />
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';

// World units: the camera sits CAMERA_HEIGHT above the network's resting plane and looks
// straight ahead, so the farther a row is, the closer it gets to the horizon line and
// the smaller its nodes and links are drawn.
export const HORIZON = 0.7; // horizon line, as a fraction of the viewport height (the code screen ends there)
const CAMERA_HEIGHT = 1;
const NEAR = 1;
const FAR = 7;
const SPACING = 0.20; // lattice step between neighbouring nodes
const JITTER = 0.2; // how far nodes stray from the lattice, in steps
const THICKNESS = 0.01; // height of the cloud around its resting plane
const NODE_RADIUS = 5; // px, for a node on the near plane
const CREST_GROWTH = 1.2; // extra node size per unit of wave height, so crests read as swells
const LINK_WIDTH = 1.2; // px, for a link on the near plane
const LINK_CHANCE = 0.0; // share of lattice neighbours that get linked
const NODE_OPACITY = 0.85;
const LINK_OPACITY = 0.35;
const PARALLAX = 0.5; // how far the camera slides sideways following the pointer
const DEPTH_BANDS = 12; // draw batches: one path per band, sharing an opacity and line width
// [amplitude, wavelength, heading (rad), angular speed]: summed swells rolling towards the camera.
const WAVES = [
    [0.22, 5.5, 1.35, 0.8],
    [0.12, 3, 1.9, 1.2],
    [0.06, 1.7, 0.6, 1.6],
];
const TAU = Math.PI * 2;

/** Deterministic noise in [0, 1) per lattice cell, so a resize rebuilds the same network. */
function noise(i, j, salt) {
    let h = Math.imul(i, 0x27d4eb2d) ^ Math.imul(j, 0x165667b1) ^ Math.imul(salt, 0x9e3779b9);
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Nodes on a jittered lattice spanning the view frustum, randomly linked to their neighbours. */
function buildNetwork(width, height) {
    // Puts the resting plane at the near distance just below the bottom edge.
    const focal = ((height * (1 - HORIZON) * NEAR) / CAMERA_HEIGHT) * 1.15;
    const depthOf = (z) => (z - NEAR) / (FAR - NEAR);
    const bands = Array.from({ length: DEPTH_BANDS }, (_, b) => {
        const z = NEAR + ((b + 0.5) / DEPTH_BANDS) * (FAR - NEAR);
        // Fog: fades the far rows out before they pile up on the horizon.
        return { alpha: (1 - depthOf(z)) ** 1.5, lineWidth: (LINK_WIDTH * NEAR) / z, nodes: [], links: [] };
    });
    const bandAt = (z) => bands[Math.min(DEPTH_BANDS - 1, Math.max(0, Math.floor(depthOf(z) * DEPTH_BANDS)))];

    const rows = [];
    const nodes = [];
    for (let j = 0; NEAR + j * SPACING < FAR; j++) {
        const rowZ = NEAR + j * SPACING;
        // Only as wide as the view at that depth, so near rows don't waste nodes off-screen.
        const half = Math.ceil(((width / 2 / focal) * rowZ + PARALLAX) / SPACING) + 1;
        const row = new Map();
        for (let i = -half; i <= half; i++) {
            const x = (i + (noise(i, j, 1) - 0.5) * JITTER) * SPACING;
            const z = rowZ + (noise(i, j, 2) - 0.5) * JITTER * SPACING;
            const node = {
                x,
                z,
                rest: (noise(i, j, 3) - 0.5) * THICKNESS,
                bob: noise(i, j, 4) * TAU,
                phases: WAVES.map(([, length, heading]) => (TAU / length) * (x * Math.cos(heading) + z * Math.sin(heading))),
                radius: (NODE_RADIUS * (0.5 + noise(i, j, 5)) * NEAR) / z,
                sx: 0,
                sy: 0,
                sr: 0,
            };
            row.set(i, node);
            nodes.push(node);
            bandAt(z).nodes.push(node);
        }
        rows.push(row);
    }

    rows.forEach((row, j) => {
        const next = rows[j + 1];
        row.forEach((node, i) => {
            [row.get(i + 1), next?.get(i), next?.get(i + 1), next?.get(i - 1)].forEach((other, k) => {
                if (other && noise(i, j, 6 + k) < LINK_CHANCE) bandAt((node.z + other.z) / 2).links.push([node, other]);
            });
        });
    });

    return { focal, nodes, bands: bands.reverse() }; // far to near, so near bands paint on top
}

/**
 * Full-screen background: a perspective cloud of linked nodes, swelling like the sea
 * across the bottom of the screen. The camera drifts a little with the pointer.
 */
export default {
    name: 'NeuralWaveBackground',
    setup() {
        const canvas = ref(null);
        const { $vuetify } = getCurrentInstance().proxy;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let ctx = null;
        let network = null;
        let width = 0;
        let height = 0;
        let cameraX = 0;
        let targetCameraX = 0;
        let frame = 0;

        function project(t) {
            cameraX += (targetCameraX - cameraX) * 0.04;
            const centerX = width / 2;
            const horizonY = height * HORIZON;
            for (const node of network.nodes) {
                let y = node.rest + 0.03 * Math.sin(t * 1.3 + node.bob);
                for (let w = 0; w < WAVES.length; w++) y += WAVES[w][0] * Math.sin(node.phases[w] + WAVES[w][3] * t);
                const scale = network.focal / node.z;
                node.sx = centerX + (node.x - cameraX) * scale;
                node.sy = horizonY + (CAMERA_HEIGHT - y) * scale;
                node.sr = node.radius * Math.max(0.3, 1 + y * CREST_GROWTH);
            }
        }

        function draw() {
            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = ctx.strokeStyle = $vuetify.theme.currentTheme.network;
            for (const band of network.bands) {
                ctx.globalAlpha = band.alpha * LINK_OPACITY;
                ctx.lineWidth = band.lineWidth;
                ctx.beginPath();
                for (const [a, b] of band.links) {
                    if (Math.max(a.sx, b.sx) < 0 || Math.min(a.sx, b.sx) > width || Math.min(a.sy, b.sy) > height) continue;
                    ctx.moveTo(a.sx, a.sy);
                    ctx.lineTo(b.sx, b.sy);
                }
                ctx.stroke();

                ctx.globalAlpha = band.alpha * NODE_OPACITY;
                ctx.beginPath();
                for (const { sx, sy, sr } of band.nodes) {
                    if (sx < -sr || sx > width + sr || sy > height + sr) continue;
                    ctx.moveTo(sx + sr, sy);
                    ctx.arc(sx, sy, sr, 0, TAU);
                }
                ctx.fill();
            }
        }

        function render(now) {
            project(now / 1000);
            draw();
        }

        function loop(now) {
            render(now);
            frame = requestAnimationFrame(loop);
        }

        function resize() {
            const el = canvas.value;
            const ratio = Math.min(window.devicePixelRatio || 1, 2);
            width = el.clientWidth;
            height = el.clientHeight;
            el.width = Math.round(width * ratio);
            el.height = Math.round(height * ratio);
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            network = buildNetwork(width, height);
            if (reducedMotion) render(0);
        }

        function onPointerMove(event) {
            targetCameraX = (event.clientX / width - 0.5) * PARALLAX;
        }

        // Without the animation loop, a theme switch has to repaint the new color itself.
        if (reducedMotion) watch(() => $vuetify.theme.currentTheme.network, () => render(0));

        onMounted(() => {
            ctx = canvas.value.getContext('2d');
            resize();
            window.addEventListener('resize', resize);
            if (reducedMotion) return;
            window.addEventListener('pointermove', onPointerMove, { passive: true });
            frame = requestAnimationFrame(loop);
        });
        onBeforeUnmount(() => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', resize);
            window.removeEventListener('pointermove', onPointerMove);
        });

        return { canvas };
    },
};
</script>

<style scoped>
.neural-wave {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
}
</style>
